import { useHistory, useParams } from "react-router-dom";
import { useState, useEffect, FormEvent } from "react";
import {
  Titulo,
  SubTitulo,
  Header,
  TituloCartao,
  Cartao,
  CorpoCartao,
  InformacaoItem,
  InformacaoRotulo,
  InformacaoConteudo,
  FormularioRotulo,
  FormularioControle,
  FormularioGrupo,
  FormularioAreaTexto,
  InputGrupo,
  Aside,
  Main,
  BotaoPrimario,
  BotaoMuted,
  Skeleton,
  MensagemErro,
  FormularioLinha,
  FormularioGrupoLinha
} from "./styles";
import { Container } from "../../styles";
import api from "../../../../../services/api";
import { IProposta } from "../../../../../utility/IProposta";
import { IOrientacaoCompleta } from "../../../../../utility/IOrientacaoCompleta";
import { definirTituloAudiencia, extrairOrientacaoAtual, mapearDadosCard, converterArquivosOrientacaoParaAnexos } from "./utils";
import ComponenteArquivosOrientacao from "./ComponenteArquivosOrientacao";
import { IAnexo } from "../../../../../utility/IAnexo";
import { useAuth } from "../../../../../contexts/UserContext";
import toastservice from "../../../../../services/toast";

export default function FormularioOrientacaoCompleta({ idDemanda }: any) {
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const { setPageType } = useAuth();
  const [anexos, setAnexos] = useState<IAnexo[]>([]);
  const [demanda, setDemanda] = useState<IProposta | null>(null);
  const [orientacaoAtual, setOrientacaoAtual] = useState<IOrientacaoCompleta | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [enviando, setEnviando] = useState<boolean>(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);
  const [erros, setErros] = useState<Record<string, string>>({});
  
  const [sessionId] = useState(() => {
    return `orient_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  });

  const demandaId = idDemanda ?? (params?.id ? Number(params.id) : undefined);

  useEffect(() => {
    setPageType('vazio');
  
    return () => {
      setPageType(undefined);
    };
  }, [setPageType]);

  useEffect(() => {
    if (!demandaId) {
      setDemanda(null);
      setOrientacaoAtual(undefined);
      return;
    }

    let ativo = true;

    const carregaDemanda = async () => {
      setLoading(true);
      try {
        const response = await api.get(`proposta/${demandaId}`);
        if (!ativo) {
          return;
        }

        if (response.data.status === 'success') {
          const resultado = response.data.result;
          setDemanda(resultado);
          setOrientacaoAtual(extrairOrientacaoAtual(resultado.orientacoes));
        } else {
          setDemanda(null);
          setOrientacaoAtual(undefined);
        }
      } catch (err) {
        if (ativo) {
          setDemanda(null);
          setOrientacaoAtual(undefined);
        }
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    };

    carregaDemanda();

    return () => {
      ativo = false;
    };
  }, [demandaId]);

  useEffect(() => {
    if (orientacaoAtual?.arquivos && Array.isArray(orientacaoAtual.arquivos)) {
      const anexosConvertidos = converterArquivosOrientacaoParaAnexos(orientacaoAtual.arquivos);
      setAnexos(anexosConvertidos);
    } else {
      setAnexos([]);
    }
  }, [orientacaoAtual]);

  const tituloAudiencia = definirTituloAudiencia(demanda);
  const dadosCard = mapearDadosCard(demanda);
  const orientacaoSelecionada = orientacaoAtual ?? undefined;
  const formularioKey = orientacaoSelecionada?.id_orientacao ? `orientacao-${orientacaoSelecionada.id_orientacao}` : 'orientacao-nova';

  const validarCampos = (dados: Record<string, string>): boolean => {
    const novosErros: Record<string, string> = {};

    if (!dados.parte_representada?.trim()) {
      novosErros.parte_representada = "O campo parte representada deve ser preenchido";
    }
    if (!dados.nome_advogado?.trim()) {
      novosErros.nome_advogado = "O campo nome do advogado deve ser preenchido";
    }
    if (!dados.oab_advogado?.trim()) {
      novosErros.oab_advogado = "O campo OAB do advogado deve ser preenchido";
    }
    if (!dados.nome_preposto?.trim()) {
      novosErros.nome_preposto = "O campo nome preposto deve ser preenchido";
    }
    if (!dados.proposta_acordo?.trim()) {
      novosErros.proposta_acordo = "O campo proposta de acordo deve ser preenchido";
    }
    if (!dados.requerimentos?.trim()) {
      novosErros.requerimentos = "O campo requerimentos deve ser preenchido";
    }
    if (!dados.documentos?.trim()) {
      novosErros.documentos = "O campo documentos deve ser preenchido";
    }
    if (!dados.testemunhas?.trim()) {
      novosErros.testemunhas = "O campo testemunhas deve ser preenchido";
    }
    if (!dados.pedidos?.trim()) {
      novosErros.pedidos = "O campo pedidos deve ser preenchido";
    }
    if (!dados.resumo_contestacao?.trim()) {
      novosErros.resumo_contestacao = "O campo resumo contestação deve ser preenchido";
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSubmit = async (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();

    if (!demandaId) {
      toastservice.error("Identificador da demanda não encontrado.");
      return;
    }

    const dadosFormulario = new FormData(evento.currentTarget);
    const obterTexto = (campo: string) => {
      const valor = dadosFormulario.get(campo);
      return typeof valor === "string" ? valor : "";
    };

    const dados = {
      parte_representada: obterTexto("parte_representada"),
      nome_advogado: obterTexto("nome_advogado"),
      oab_advogado: obterTexto("oab_advogado"),
      nome_preposto: obterTexto("nome_preposto"),
      proposta_acordo: obterTexto("proposta_acordo"),
      requerimentos: obterTexto("requerimentos"),
      documentos: obterTexto("documentos"),
      testemunhas: obterTexto("testemunhas"),
      pedidos: obterTexto("pedidos"),
      resumo_contestacao: obterTexto("resumo_contestacao"),
      outras_informacoes: obterTexto("outras_informacoes")
    };

    if (!validarCampos(dados)) {
      toastservice.error("Preencha os campos corretamente.");
      return;
    }

    const arquivos = anexos
      .map((anexo) => {
        const caminho = anexo.caminho || anexo.arquivo_final;
        if (!caminho) {
          return null;
        }

        const arquivoData = {
          caminho,
          id_orientacao_arquivo: (anexo as any).id_orientacao_arquivo,
          id_proposta_arquivo: anexo.id_proposta_arquivo,
          id_proposta: anexo.id_proposta
        };
        
        return arquivoData;
      })
      .filter((item) => item !== null) as Array<{
        caminho: string;
        id_orientacao_arquivo?: number;
        id_proposta_arquivo?: number;
        id_proposta?: number;
      }>;

    const payload = {
      id_proposta: demandaId,
      ...dados,
      arquivos
    };

    try {
      setEnviando(true);

      if (orientacaoSelecionada?.id_orientacao) {
        await api.put(`orientacao/${orientacaoSelecionada.id_orientacao}`, payload);
      } else {
        await api.post("orientacao", payload);
      }

      history.push(`/demandas/${demandaId}/orientacoes`);
    } catch (erro: any) {
      const mensagem =
        erro?.response?.data?.message ||
        erro?.response?.data?.result?.[0]?.error ||
        erro?.message ||
        "Não foi possível salvar a orientação.";
      toastservice.error(mensagem);
    } finally {
      setEnviando(false);
    }
  };

  const handleCancelar = () => {
    if (demandaId) {
      history.push(`/demandas/${demandaId}/orientacoes`);
    }
  };

  if (!demandaId) {
    return null;
  }

  if (loading) {
    return (
      <div>
        <Header>
          <Titulo>Orientações sobre a demanda #{demandaId ?? '--'}</Titulo>
          <SubTitulo>Instrua o profissional para uma realização adequada.</SubTitulo>
        </Header>

        <Container>
          <Aside>
            <Cartao>
              <CorpoCartao>
                <Skeleton style={{ height: '28px', width: '65%', marginBottom: '16px' }} />
                
                <div style={{ marginBottom: '16px' }}>
                  <Skeleton style={{ height: '20px', width: '50%', marginBottom: '4px' }} />
                  <Skeleton style={{ height: '20px', width: '85%' }} />
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <Skeleton style={{ height: '20px', width: '70%', marginBottom: '4px' }} />
                  <Skeleton style={{ height: '20px', width: '95%' }} />
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <Skeleton style={{ height: '20px', width: '45%', marginBottom: '4px' }} />
                  <Skeleton style={{ height: '20px', width: '60%' }} />
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <Skeleton style={{ height: '20px', width: '40%', marginBottom: '4px' }} />
                  <Skeleton style={{ height: '20px', width: '55%' }} />
                </div>
                
                <div style={{ marginBottom: '0' }}>
                  <Skeleton style={{ height: '20px', width: '75%', marginBottom: '4px' }} />
                  <Skeleton style={{ height: '20px', width: '50%' }} />
                </div>
              </CorpoCartao>
            </Cartao>
          </Aside>
          <Main>
            <Cartao>
              <CorpoCartao>
                <Skeleton style={{ height: '20px', width: '60%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '40px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '50%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '40px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '45%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '40px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '50%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '40px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '55%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '100px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '75%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '100px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '50%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '100px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '120px', width: '100%', border: '2px dashed #CCD6DB', borderRadius: '8px', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '55%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '100px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '70%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '100px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '60%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '100px', width: '100%', marginBottom: '16px' }} />
                
                <Skeleton style={{ height: '20px', width: '55%', marginBottom: '8px' }} />
                <Skeleton style={{ height: '100px', width: '100%', marginBottom: '16px' }} />
                
                <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                  <Skeleton style={{ height: '40px', width: '120px' }} />
                  <Skeleton style={{ height: '40px', width: '120px' }} />
                </div>
              </CorpoCartao>
            </Cartao>
          </Main>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <Header>
        <Titulo>Orientações sobre a demanda #{demandaId ?? '--'}</Titulo>
        <SubTitulo>Instrua o profissional para uma realização adequada.</SubTitulo>
      </Header>

      <Container>
        <Aside>
          <Cartao>
            <CorpoCartao>
              <TituloCartao>{tituloAudiencia}</TituloCartao>
              <InformacaoItem>
                <InformacaoRotulo>COMARCA:</InformacaoRotulo>
                <InformacaoConteudo>{dadosCard.comarca}</InformacaoConteudo>
              </InformacaoItem>
              <InformacaoItem>
                <InformacaoRotulo>NÚMERO DO PROCESSO:</InformacaoRotulo>
                <InformacaoConteudo>{dadosCard.numeroProcesso}</InformacaoConteudo>
              </InformacaoItem>
              <InformacaoItem>
                <InformacaoRotulo>MODALIDADE:</InformacaoRotulo>
                <InformacaoConteudo>{dadosCard.modalidade}</InformacaoConteudo>
              </InformacaoItem>
              <InformacaoItem>
                <InformacaoRotulo>SUBTIPO:</InformacaoRotulo>
                <InformacaoConteudo>{dadosCard.subtipo}</InformacaoConteudo>
              </InformacaoItem>
              <InformacaoItem>
                <InformacaoRotulo>PROFISSIONAL NECESSÁRIO:</InformacaoRotulo>
                <InformacaoConteudo>{dadosCard.profissionalNecessario}</InformacaoConteudo>
              </InformacaoItem>
            </CorpoCartao>
          </Cartao>
        </Aside>

        <Main>
          <Cartao>
            <CorpoCartao>
              <form key={formularioKey} onSubmit={handleSubmit}>
                <FormularioGrupo>
                  <FormularioRotulo obrigatorio>Qual parte representamos?</FormularioRotulo>
                  <FormularioControle
                    name="parte_representada"
                    type="text"
                    placeholder="Digite a parte representadas..."
                    defaultValue={orientacaoSelecionada?.parte_representada || ""}
                    erro={!!erros.parte_representada}
                  />
                  {erros.parte_representada && <MensagemErro>{erros.parte_representada}</MensagemErro>}
                </FormularioGrupo>

                <FormularioLinha>
                  <FormularioGrupoLinha>
                    <FormularioRotulo obrigatorio>Nome do advogado:</FormularioRotulo>
                    <FormularioControle
                      name="nome_advogado"
                      type="text"
                      placeholder="Digite o nome do advogado..."
                      defaultValue={orientacaoSelecionada?.nome_advogado || ""}
                      erro={!!erros.nome_advogado}
                    />
                    {erros.nome_advogado && <MensagemErro>{erros.nome_advogado}</MensagemErro>}
                  </FormularioGrupoLinha>

                  <FormularioGrupoLinha>
                    <FormularioRotulo obrigatorio>OAB do advogado:</FormularioRotulo>
                    <FormularioControle
                      name="oab_advogado"
                      type="text"
                      placeholder="Digite a OAB do advogado..."
                      defaultValue={orientacaoSelecionada?.oab_advogado || ""}
                      erro={!!erros.oab_advogado}
                    />
                    {erros.oab_advogado && <MensagemErro>{erros.oab_advogado}</MensagemErro>}
                  </FormularioGrupoLinha>
                </FormularioLinha>

                <FormularioGrupo>
                  <FormularioRotulo obrigatorio>Nome do preposto:</FormularioRotulo>
                  <FormularioControle
                    name="nome_preposto"
                    type="text"
                    placeholder="Digite o nome do preposto..."
                    defaultValue={orientacaoSelecionada?.nome_preposto || ""}
                    erro={!!erros.nome_preposto}
                  />
                  {erros.nome_preposto && <MensagemErro>{erros.nome_preposto}</MensagemErro>}
                </FormularioGrupo>

                <FormularioGrupo>
                  <FormularioRotulo obrigatorio>Proposta de acordo:</FormularioRotulo>
                  <FormularioAreaTexto
                    name="proposta_acordo"
                    placeholder="Descreva a proposta de acordo..."
                    defaultValue={orientacaoSelecionada?.proposta_acordo || ""}
                    erro={!!erros.proposta_acordo}
                  />
                  {erros.proposta_acordo && <MensagemErro>{erros.proposta_acordo}</MensagemErro>}
                </FormularioGrupo>

                <FormularioGrupo>
                  <FormularioRotulo obrigatorio>Requerimentos a realizar em audiência:</FormularioRotulo>
                  <FormularioAreaTexto
                    name="requerimentos"
                    placeholder="Descreva os requerimentos..."
                    defaultValue={orientacaoSelecionada?.requerimentos || ""}
                    erro={!!erros.requerimentos}
                  />
                  {erros.requerimentos && <MensagemErro>{erros.requerimentos}</MensagemErro>}
                </FormularioGrupo>

                <FormularioGrupo>
                  <FormularioRotulo obrigatorio>Documentos a juntar:</FormularioRotulo>
                  <FormularioAreaTexto
                    name="documentos"
                    placeholder="Liste os documentos a serem juntados..."
                    defaultValue={orientacaoSelecionada?.documentos || ""}
                    erro={!!erros.documentos}
                  />
                  {erros.documentos && <MensagemErro>{erros.documentos}</MensagemErro>}
                  <ComponenteArquivosOrientacao
                    idProposta={demandaId}
                    idOrientacao={orientacaoSelecionada?.id_orientacao}
                    anexosIniciais={anexos}
                    aoAtualizarLista={setAnexos}
                    aoUploadingChange={setIsUploadingFiles}
                    sessionId={sessionId}
                  />
                </FormularioGrupo>

                <FormularioGrupo>
                  <FormularioRotulo obrigatorio>Testemunhas a ouvir:</FormularioRotulo>
                  <FormularioAreaTexto
                    name="testemunhas"
                    placeholder="Liste as testemunhas..."
                    defaultValue={orientacaoSelecionada?.testemunhas || ""}
                    erro={!!erros.testemunhas}
                  />
                  {erros.testemunhas && <MensagemErro>{erros.testemunhas}</MensagemErro>}
                </FormularioGrupo>

                <FormularioGrupo>
                  <FormularioRotulo obrigatorio>Pedidos relevantes da inicial:</FormularioRotulo>
                  <FormularioAreaTexto
                    name="pedidos"
                    placeholder="Descreva os pedidos..."
                    defaultValue={orientacaoSelecionada?.pedidos || ""}
                    erro={!!erros.pedidos}
                  />
                  {erros.pedidos && <MensagemErro>{erros.pedidos}</MensagemErro>}
                </FormularioGrupo>

                <FormularioGrupo>
                  <FormularioRotulo obrigatorio>Resumo da contestação:</FormularioRotulo>
                  <FormularioAreaTexto
                    name="resumo_contestacao"
                    placeholder="Resuma a contestação..."
                    defaultValue={orientacaoSelecionada?.resumo_contestacao || ""}
                    erro={!!erros.resumo_contestacao}
                  />
                  {erros.resumo_contestacao && <MensagemErro>{erros.resumo_contestacao}</MensagemErro>}
                </FormularioGrupo>

                <FormularioGrupo>
                  <FormularioRotulo>Outras informações:</FormularioRotulo>
                  <FormularioAreaTexto
                    name="outras_informacoes"
                    placeholder="Informações adicionais..."
                    defaultValue={orientacaoSelecionada?.outras_informacoes || ""}
                  />
                </FormularioGrupo>

                <InputGrupo>
                  <BotaoMuted type="button" onClick={handleCancelar}>Cancelar</BotaoMuted>
                  <BotaoPrimario type="submit" disabled={enviando || isUploadingFiles}>
                    {enviando ? "Enviando..." : isUploadingFiles ? "Enviando arquivos..." : "Enviar"}
                  </BotaoPrimario>
                </InputGrupo>
              </form>
            </CorpoCartao>
          </Cartao>
        </Main>
      </Container>
    </div>
  );
}

